"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  BarChart3,
  BookOpen,
  CircleHelp,
  CreditCard,
  KeyRound,
  LogOut,
  MessageSquare,
  ReceiptText,
  Tags,
  X,
} from "lucide-react";
import { DeepSeekLogo, DeepSeekWordmark } from "./icons";

const MAIN_ITEMS = [
  { label: "用量信息", icon: BarChart3, href: "/" },
  { label: "API keys", icon: KeyRound, href: "/api-keys" },
  { label: "充值", icon: CreditCard, href: "/recharge" },
  { label: "账单", icon: ReceiptText, href: "/bills" },
];

const FOOTER_ITEMS = [
  { label: "接口文档", icon: BookOpen, href: "https://api-docs.deepseek.com/zh-cn/" },
  { label: "帮助与反馈", icon: CircleHelp, href: "https://platform.deepseek.com/help" },
  { label: "产品定价", icon: Tags, href: "https://platform.deepseek.com/pricing" },
  { label: "网页版免费对话", icon: MessageSquare, href: "https://chat.deepseek.com/" },
];

type SidebarItem = (typeof MAIN_ITEMS)[number];

export function UsageSidebar({ phone, onClose }: { phone?: string; onClose?: () => void }) {
  const pathname = usePathname();

  const handleLogout = async () => {
    try {
      await fetch("/api/auth/logout", { method: "POST" });
    } finally {
      window.location.href = "/";
    }
  };

  const renderNavItem = ({ label, icon: Icon, href }: SidebarItem) => {
    const external = href.startsWith("http");
    const active = !external && pathname === href;
    const cls = `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors ${
      active
        ? "bg-[#f0f2ff] font-medium text-[#4d6bfe]"
        : "text-[#545557] hover:bg-[#f5f6f8] hover:text-[#212327]"
    }`;
    const inner = (
      <>
        <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
        <span className="flex-1">{label}</span>
        {external && (
          <svg viewBox="0 0 24 24" fill="none" className="h-3.5 w-3.5 text-[#a2a4a6]">
            <path
              d="M7 17 17 7M9 7h8v8"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </>
    );
    return external ? (
      <a key={label} href={href} target="_blank" rel="noopener noreferrer" className={cls}>
        {inner}
      </a>
    ) : (
      <Link key={label} href={href} className={cls} onClick={onClose}>
        {inner}
      </Link>
    );
  };

  return (
    <aside className="flex h-full w-full flex-col border-r border-[#f0f1f3] bg-white md:w-60">
      {/* brand */}
      <div className="flex h-14 items-center gap-2 border-b border-[#f0f1f3] px-4">
        <DeepSeekLogo className="h-7 w-7" />
        <DeepSeekWordmark className="hidden h-5 md:block" />
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded-md p-1.5 text-[#7f8287] hover:bg-[#f5f5f5] md:hidden"
            aria-label="关闭菜单"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* nav */}
      <nav className="flex-1 space-y-0.5 overflow-y-auto p-2" aria-label="主导航">
        {MAIN_ITEMS.map(renderNavItem)}
      </nav>

      {/* external links above profile */}
      <div className="border-t border-[#f0f1f3] p-2">
        <div className="space-y-0.5">{FOOTER_ITEMS.map(renderNavItem)}</div>
      </div>

      {/* profile */}
      <div className="flex items-center gap-2.5 border-t border-[#f0f1f3] p-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#4d6bfe] text-sm font-semibold text-white">
          {phone ? phone.slice(-2) : "Q"}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-[#212327]">{phone ?? "个人信息"}</div>
          <div className="truncate text-xs text-[#a2a4a6]">登录管理</div>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="rounded-md p-1.5 text-[#9ca3af] transition-colors hover:bg-[#f5f5f5] hover:text-[#f04438]"
          aria-label="退出登录"
          title="退出登录"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </aside>
  );
}
